<script setup>
/* global axios */
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  emitOnClick: {
    type: Boolean,
    default: false,
  },
  highlightDealId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['deal-click']);

const route = useRoute();
const router = useRouter();

const accountId = computed(() => route.params.accountId);

const deals = ref([]);
const isLoading = ref(false);
const hasError = ref(false);

const isEmpty = computed(
  () => !isLoading.value && deals.value.length === 0
);

const statusConfig = {
  open: {
    label: 'Aberto',
    class:
      'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400',
  },
  won: {
    label: 'Ganho',
    class:
      'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400',
  },
  lost: {
    label: 'Perdido',
    class:
      'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400',
  },
};

const fetchDeals = async () => {
  if (!props.contactId || !accountId.value) return;

  isLoading.value = true;
  hasError.value = false;

  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/contacts/${props.contactId}/deals`
    );
    deals.value = response.data?.payload || [];
  } catch {
    hasError.value = true;
    deals.value = [];
  } finally {
    isLoading.value = false;
  }
};

const formatCurrency = value => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const formatDate = dateStr => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const navigateToDeal = deal => {
  if (props.emitOnClick) {
    emit('deal-click', deal);
    return;
  }
  router.push({
    name: 'crm_item_detail',
    params: { accountId: accountId.value, itemId: deal.id },
  });
};

watch(
  () => props.contactId,
  newId => {
    if (newId) {
      deals.value = [];
      fetchDeals();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div :class="compact ? 'px-4 py-2' : 'px-6 py-4'">
    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Error -->
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center py-8 gap-2"
    >
      <span class="i-lucide-alert-circle size-6 text-n-slate-10" />
      <p class="text-sm text-n-slate-10 mb-0">
        Erro ao carregar negocios.
      </p>
      <button
        class="text-xs text-woot-500 hover:text-woot-600 font-medium border-0 bg-transparent cursor-pointer"
        @click="fetchDeals"
      >
        Tentar novamente
      </button>
    </div>

    <!-- Empty -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-8 gap-1.5"
    >
      <span class="i-lucide-briefcase size-6 text-n-slate-9" />
      <p class="text-sm text-n-slate-10 mb-0">
        Nenhum negocio associado a este contato.
      </p>
    </div>

    <!-- Deal list -->
    <transition-group
      v-else
      name="deal-fade"
      tag="div"
      class="flex flex-col gap-2"
    >
      <button
        v-for="deal in deals"
        :key="deal.id"
        class="deal-item flex flex-col gap-1.5 p-3 rounded-lg border border-n-weak bg-n-alpha-black2 hover:bg-n-alpha-2 cursor-pointer text-left w-full"
        :class="{ 'deal-highlight': deal.id === highlightDealId }"
        @click="navigateToDeal(deal)"
      >
        <div class="flex items-center justify-between w-full">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ deal.title }}
          </span>
          <span
            v-if="deal.status"
            class="text-[10px] font-semibold px-1.5 py-0.5 rounded-md flex-shrink-0"
            :class="statusConfig[deal.status]?.class || statusConfig.open.class"
          >
            {{ statusConfig[deal.status]?.label || deal.status }}
          </span>
        </div>
        <div class="flex items-center gap-2 text-xs text-n-slate-10">
          <span v-if="deal.pipeline_name" class="truncate">
            {{ deal.pipeline_name }}
          </span>
          <span v-if="deal.pipeline_name && deal.stage_name">-</span>
          <span v-if="deal.stage_name" class="truncate">
            {{ deal.stage_name }}
          </span>
        </div>
        <div class="flex items-center justify-between w-full text-xs">
          <span class="font-medium text-n-slate-11">
            {{ formatCurrency(deal.total_value) }}
          </span>
          <span class="text-n-slate-9">
            {{ formatDate(deal.created_at) }}
          </span>
        </div>
      </button>
    </transition-group>
  </div>
</template>

<style scoped>
.deal-item {
  transition:
    background-color 1.5s ease-out,
    border-color 1.5s ease-out;
}

.deal-fade-enter-active {
  transition: all 0.4s ease-out;
}
.deal-fade-enter-from {
  opacity: 0;
  transform: translateY(-12px);
}

.deal-highlight {
  background-color: rgba(34, 197, 94, 0.18);
  border-color: rgba(34, 197, 94, 0.5);
  animation: pulse-highlight 0.7s ease-out;
}

@keyframes pulse-highlight {
  0% {
    background-color: rgba(34, 197, 94, 0.4);
    border-color: rgba(34, 197, 94, 0.9);
  }
  100% {
    background-color: rgba(34, 197, 94, 0.18);
    border-color: rgba(34, 197, 94, 0.5);
  }
}
</style>
